import React from 'react';
import { useIntl } from 'react-intl';
import { Alert, Button } from 'reactstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck } from '@fortawesome/pro-light-svg-icons/faCheck';
import { TypedMemo } from '../../../common/types';
import { accountMessages } from './accountMessages';
import { useEmailChange } from './useEmailChange';
import { EmailChangeForm } from './EmailChangeForm';
import { Panel, PanelHead } from '../../../music24/components/primitives/Panel';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the Settings tabs; each part uses its own slice of it */
import styles from './account.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface Props {
  email: string;
  onEmailChanged: () => Promise<unknown>;
}

/** The sign-in card: the address, and the email change in place (design settings-redesign b1, b2). */
const AccountSignInCardComponent: React.FC<Props> = ({ email, onEmailChanged }) => {
  const intl = useIntl();
  const change = useEmailChange(onEmailChanged);
  const isEditing = change.step === 'enterNewEmail' || change.step === 'enterCode';

  const subtitle =
    change.step === 'enterNewEmail'
      ? intl.formatMessage(accountMessages.stepNewEmail)
      : change.step === 'enterCode'
        ? intl.formatMessage(accountMessages.stepCode, { email: change.newEmail })
        : intl.formatMessage(accountMessages.signInDescription);

  return (
    <Panel as="section">
      <PanelHead
        title={intl.formatMessage(accountMessages.signInTitle)}
        subtitle={<span data-clarity-mask="true">{subtitle}</span>}
      />
      {isEditing ? (
        <EmailChangeForm change={change} />
      ) : (
        <div className={styles.identity}>
          <div className={styles.avatar} aria-hidden="true">
            {email.charAt(0).toUpperCase()}
          </div>
          <div className={styles.identityText}>
            <span className={styles.email} data-clarity-mask="true">
              {email}
            </span>
            <span className={styles.note}>{intl.formatMessage(accountMessages.addressNote)}</span>
          </div>
          <Button color="secondary" outline size="sm" onClick={change.start}>
            {intl.formatMessage({ id: 'app.sml.settings.account.changeEmail', defaultMessage: 'Change email' })}
          </Button>
          {change.step === 'success' && (
            <Alert color="success" className="w-100 mb-0 d-flex align-items-center gap-2" toggle={change.finish}>
              <FontAwesomeIcon icon={faCheck} />
              <span data-clarity-mask="true">
                {intl.formatMessage(accountMessages.emailChangedNow, { email: change.newEmail })}
              </span>
            </Alert>
          )}
        </div>
      )}
    </Panel>
  );
};

export const AccountSignInCard = TypedMemo(AccountSignInCardComponent);
